/**
 * Решение по посту. Раньше это была третья колонка экрана черновика; с 26.08.2026 —
 * ДВЕ ЧАСТИ, и деление между ними смысловое, а не вёрсточное (решение заказчика):
 *
 *   • **что известно** — проверки, слот и ключ, результат публикации, экран несостоявшейся
 *     публикации. Это чтение: длинное, разное в разных состояниях, место ему в потоке
 *     страницы под превью;
 *   • **что можно сделать** — сами кнопки. Это полоса действий, закреплённая у нижнего края
 *     области черновика (`position: sticky`).
 *
 * ПОЧЕМУ КНОПКИ ВНИЗУ И ЗАКРЕПЛЕНЫ. Третья колонка расширяла экран и заставляла листать
 * вбок, а утверждение — самое частое и самое ответственное действие: его место обязано быть
 * одним и тем же в любом режиме экрана и при любой длине поста. Полоса внизу это и даёт:
 * дочитал пост — кнопка под рукой, не дочитал — она всё равно на месте. Приклеенный к
 * колонке блок так не умеет: он уезжает вместе с колонкой.
 *
 * Компонент возвращает ФРАГМЕНТ из двух блоков подряд — контекст и полосу. Фрагмент не
 * создаёт узла, поэтому оба блока становятся прямыми детьми `.panel` экрана черновика, и
 * областью прилипания полосы оказывается весь экран, а не промежуточная обёртка. Обёртка
 * вокруг этих двух блоков сломала бы `sticky` молча — она и есть то, чего здесь нет.
 * Разбивать состояние (`busy`, `error`, открытый диалог) на два компонента тоже нельзя: это
 * состояние ОДНОГО действия, и второй его владелец разошёлся бы с первым.
 *
 * Остальное здесь не обсуждается и осталось прежним:
 *
 * 1. **Набор действий определяется состоянием версии, и ничем больше.** Список состояний и
 *    рёбер — `DRAFT_TRANSITIONS` в `@smm/contracts`; кнопки, которой не соответствует ребро,
 *    здесь нет. Нарисовать кнопку, которую бэкенд встретит 409, значит соврать человеку.
 * 2. **Одно primary-действие в смысловом блоке** (docs/ui-design.md §7). Остальное — ghost.
 * 3. **После каждого успешного действия дёргается `onChanged()`.** Он обновляет и панель, и
 *    список слева: один владелец данных — один refetch (см. `WorkspacePage`).
 *
 * Зона бренда приходит по HTTP (`GET /api/v1/brands`) и никак иначе: у `packages/ui` из
 * `@smm/*` разрешён только `contracts` (правило R4), конфиг бренда ему физически недоступен,
 * а без зоны момент слота нечем подписать (09 §9.4).
 *
 * БРЕНД ПОЛОСЫ — `model.brand`, а не адрес страницы (2026-10-25, изоляция брендов): режим, зона,
 * сервисы, канал, слоты одобрения, тестовая отправка и отзыв — всё про бренд черновика.
 * Бренд адреса (`screenBrand`) уходит ровно в одно место — `expected_brand` одобрения, отзыва и
 * тестовой отправки: бэкенд сверяет его с брендом строки черновика, и расхождение значит, что
 * экран остался без перехода `DraftPanel` на адрес бренда черновика. Отправь полоса туда
 * `model.brand`, сверка сравнивала бы строку черновика саму с собой и не ловила бы ничего.
 */

import { isoToMs, msToWallMinute, nowMs } from '@smm/contracts';
import { type JSX, type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { ApiRequestError, post } from '../../api/client.ts';
import type { BrandsResponse, Publication, TestSend } from '../../api/types.ts';
import { useResource } from '../../api/useResource.ts';
import { agentsOffReason, offLabel, useBrands } from '../../brand.tsx';
import { momentMsk } from '../../components/format.ts';
import { IconExternal } from '../../components/icons.tsx';
import { useShellSettings } from '../../components/shellSettings.ts';
import { type DecisionPanelProps, hasBlocking, type UiIssue } from '../contracts.ts';
import { ApproveDialog } from './ApproveDialog.tsx';
import styles from './DecisionPanel.module.css';
import { DiscardDialog } from './DiscardDialog.tsx';
import { channelUrl, FailedPanel } from './FailedPanel.tsx';
import { outcomePhrase, retractionPhrase } from './phrases.ts';
import { RequestChangesDialog } from './RequestChangesDialog.tsx';
import { RetractDialog } from './RetractDialog.tsx';
import { slotLabel } from './SlotPicker.tsx';
import { TestSendDialog, testSendLink } from './TestSendDialog.tsx';
import {
  deviated,
  deviationText,
  isStalled,
  isTerminal,
  outcomeText,
  TEST_SEND_STATE_WORD,
  TEST_SEND_STATUSES,
  useTestSendTracker,
} from './testSend.ts';

type Dialog = 'none' | 'approve' | 'rework' | 'retract' | 'discard';

/**
 * Режим публикаций из ответа `GET /api/v1/publication-dispatch` — по полю, а не схемой: полосе
 * нужен один бит «боевой ли сейчас режим», и чужой ответ обязан давать «неизвестно» (кнопки
 * тестовой отправки нет), а не исключение посреди полосы решения.
 */
function modeOf(data: unknown): 'live' | 'test' | undefined {
  if (typeof data !== 'object' || data === null) return undefined;
  const s = (data as Record<string, unknown>)['state'];
  return s === 'live' || s === 'test' ? s : undefined;
}

/**
 * Все отправки версии в тестовый канал — подсказкой мета-строки, свежая первой: когда и чем
 * кончилась, у отказа — причина (§9.1: «подсказка у неудачи»).
 */
function testSendsTitle(sends: readonly TestSend[], now: number): string {
  const lines = sends.map((t) => {
    const when = momentMsk(t.finished_at ?? t.created_at, now);
    if (t.state === 'sent') {
      return deviated(t) ? `${when} — отправлено, но ${deviationText(t)}` : `${when} — отправлено`;
    }
    if (isTerminal(t.state)) return `${when} — ${TEST_SEND_STATE_WORD[t.state]}: ${outcomeText(t)}`;
    return `${when} — ${TEST_SEND_STATE_WORD[t.state]}`;
  });
  return ['Отправки этой версии в тестовый канал:', ...lines].join('\n');
}

/**
 * Состояния, из которых есть ребро в `discarded` (переходы 27–30). Список повторяет
 * `DISCARDABLE` из `services/backend/src/domain/decisions.ts` и обязан с ним совпадать: это
 * тот же самый набор рёбер `DRAFT_TRANSITIONS`, прочитанный с двух сторон провода. Кнопка,
 * которую бэкенд встретит 409, — это ложь человеку (см. пункт 1 в шапке файла).
 */
const DISCARDABLE = new Set(['pending_review', 'changes_requested', 'deferred', 'approved']);

/**
 * Левая группа полосы — переключение режима экрана (чтение / правка / сравнение). Владеет
 * режимом `DraftPanel`, поэтому сюда он приходит готовой разметкой.
 *
 * Расширение объявлено здесь, а не в `routes/contracts.ts`: тот файл описывает контакт
 * ТРЁХ панелей экрана и в задаче на владение не входит. Место для этого поля — там, рядом
 * с `DecisionPanelProps`; вынесено в отчёт.
 */
export interface DecisionScreenProps extends DecisionPanelProps {
  /**
   * Бренд адреса (экрана) — ТОЛЬКО для `expected_brand`; режим, канал, слоты, зона и сервисы —
   * `model.brand` (шапка файла).
   */
  readonly screenBrand: string;
  readonly leading?: ReactNode;
  /**
   * Перечитать экран — черновик и модель рендера — без списка слева и без сброса набранной
   * правки. Нужен тестовой отправке: её исход меняет мету версии, а не черновик, и полный
   * `onChanged` посреди правки текста увёл бы превью с набранного на сохранённое; а отказ
   * «версия уже не текущая» обязан увести экран на текущую, и её видно только из списка
   * версий черновика. Нет — зовётся `onChanged`.
   */
  readonly onRefresh?: () => void;
  /**
   * Политика бренда «все посты — расширенным сообщением» СЕЙЧАС; `undefined` — неизвестна.
   * Нужна сводке одобрения: версия, подписанная расширенной по политике, которую потом
   * выключили, не может объяснять себя «все посты уходят расширенным» (ревью, ui-10).
   */
  readonly richAlways?: boolean | undefined;
}

/** Строка «что известно» у версии, одобренной автоодобрением, а не человеком. */
export const AUTO_ISSUED_LINE = 'одобрение: автоматическое — человек эту версию не одобрял';

/** Состояния, в которых работа публикации уже есть и её исход показывает `FailedPanel`. */
const PUBLICATION_TROUBLE = new Set(['publishing', 'needs_manual_check', 'failed', 'expired']);

/**
 * Канал работы словами — режим, в котором пост одобрен (`Publication.channel`). Он входит в
 * подпись и после одобрения не меняется, поэтому стоит в «что известно», а не спрашивается у
 * текущего режима: пост, одобренный в тестовом режиме, остаётся тестовым и после переключения.
 */
function channelWord(p: Publication): string {
  if (p.channel === 'live') return 'боевой';
  if (p.channel === 'test') return 'тестовый';
  return 'не из конфига бренда';
}

const issueClass = (level: UiIssue['level']): string =>
  level === 'blocking' ? 'val val-err' : level === 'warning' ? 'val val-warn' : 'val val-info';

export function DecisionPanel({
  model,
  screenBrand,
  issues,
  onChanged,
  onRefresh,
  richAlways,
  leading = null,
}: DecisionScreenProps): JSX.Element {
  const brand = model.brand;
  const [dialog, setDialog] = useState<Dialog>('none');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const brands = useResource<BrandsResponse>('/api/v1/brands');
  // Переделку пишет сервис генерации; у бренда без него «Переделать…» гаснет с причиной, а не
  // ставит черновик «на переделку», из которой его некому вывести (docs/multi-brand.md §8).
  const reworkOff = agentsOffReason(useBrands(), brand, ['generation']);

  /*
   * ТЕСТОВАЯ ОТПРАВКА (docs/release-2026-09-19.md §9.1) — только в БОЕВОМ режиме: в тестовом
   * само одобрение и есть отправка в тестовый канал. Режим читается здесь, а не берётся у
   * шапки пропом: общего стора нет (`shellSettings.ts`), а переключение режима шапка сообщает
   * счётчиком `rev` — по нему полоса перечитывает режим, и кнопка появляется или уходит сразу.
   */
  const dispatch = useResource<unknown>(
    `/api/v1/publication-dispatch?brand=${encodeURIComponent(brand)}`,
  );
  const { refetch: refetchDispatch } = dispatch;
  const { rev } = useShellSettings();
  const seenRev = useRef(rev);
  useEffect(() => {
    if (seenRev.current === rev) return;
    seenRev.current = rev;
    refetchDispatch();
  }, [rev, refetchDispatch]);
  const live = modeOf(dispatch.data) === 'live';

  const tracker = useTestSendTracker({
    expectedBrand: screenBrand,
    draftId: model.draft_id,
    versionNo: model.version_no,
    contentHash: model.content_hash,
    sends: model.test_sends ?? [],
    onSettled: onRefresh ?? onChanged,
  });
  const [testSendOpen, setTestSendOpen] = useState(false);
  // Версия сменилась под открытым диалогом (сохранили в соседней вкладке) — диалог закрывается:
  // удержание отправило бы уже не ту версию, чью сводку человек читал.
  const versionKey = `${model.draft_id}:${String(model.version_no)}`;
  const [testSendFor, setTestSendFor] = useState(versionKey);
  if (testSendFor !== versionKey) {
    setTestSendFor(versionKey);
    setTestSendOpen(false);
  }
  // Версию под открытым диалогом заменили или поставили в слот, а за отправкой не следим: её
  // уже не отправить, и удержание получило бы тот же отказ. Адрес с номером версии держит
  // экран на ней и после перечитывания, поэтому ключа версии для этого мало (ревью, ui-12).
  if (testSendOpen && tracker.tracked === null && !TEST_SEND_STATUSES.has(model.status)) {
    setTestSendOpen(false);
  }
  const { forget: forgetTestSend } = tracker;
  // Функция живёт всю жизнь полосы: `Modal` держит фокус эффектом от `onClose`, и новая
  // функция на каждом опросе хода уводила бы фокус с удерживаемой кнопки (docs/frontend.md).
  const closeTestSend = useCallback((): void => {
    setTestSendOpen(false);
    forgetTestSend();
  }, [forgetTestSend]);

  const timezone = brands.data?.find((b) => b.id === brand)?.timezone;
  const blocked = hasBlocking(issues);
  const blocking = issues.filter((i) => i.level === 'blocking');
  const publication = model.publication;
  const trouble = PUBLICATION_TROUBLE.has(model.status);
  // Версию одобрило автоодобрение бренда (docs/ai-lab.md): «что известно» говорит, что решения
  // человека по ней не было, — рядом с ключом и с результатом публикации.
  const autoIssued = model.auto_approval?.state === 'issued';

  /** Момент в зоне бренда. Без зоны показываем ISO как есть — врать про местное время нельзя. */
  const inBrandZone = (iso: string): string =>
    timezone === undefined ? iso : slotLabel(msToWallMinute(isoToMs(iso), timezone));

  // `postpone`, `unschedule` и `reopen` отвечают одинаково — `{status}`. Схем на них в
  // `api/types.ts` не реэкспортировано, а заводить их здесь значило бы описать провод
  // второй раз: единственное, что нужно этому коду, — что запрос удался.
  const act = (path: string, body: Record<string, unknown>): void => {
    if (busy) return;
    setBusy(true);
    setError(null);
    post<{ readonly status: string }>(
      `/api/v1/drafts/${model.draft_id}/versions/${model.version_no}/${path}`,
      body,
    )
      .then(() => {
        onChanged();
      })
      .catch((e: unknown) => {
        if (e instanceof ApiRequestError && e.status === 409) {
          setError(`${e.message}. Обновите экран.`);
          onChanged();
        } else {
          setError(e instanceof Error ? e.message : String(e));
        }
      })
      .finally(() => {
        setBusy(false);
      });
  };

  // ── что известно: блок в потоке страницы, над полосой ──────────────────────────────

  /** Пояснения к состоянию. Всё, что объясняет, а не действует, живёт здесь. */
  const stateNote = (): ReactNode => {
    switch (model.status) {
      case 'approved':
        return (
          <div className="micro">
            Версия одобрена, но слота у неё нет: ключ публикации выпускается вместе со слотом.
          </div>
        );

      case 'deferred':
        return (
          <div className="micro">
            Отложенная версия ждёт человека: сама она в очередь не вернётся.
          </div>
        );

      case 'changes_requested':
        // Заголовка «На переделке» здесь больше нет: состояние версии уже названо пилюлей в
        // шапке экрана, и третье его повторение (пилюля → заголовок → текст) было ровно тем
        // лишним текстом, на который жаловался Илья 07.09. Объяснение оставлено целиком —
        // оно говорит не «в каком состоянии пост», а «что теперь будет и чего ждать».
        //
        // «В фазе 1 сервиса генерации ещё нет» снято 2026-11-02: сервис пишет переделки с фазы
        // 2, и на этот текст теперь попадает и отправленное мнение автора (полоса «Мнение
        // автора»), — первым словом оно и названо: передано мнение, а не замечание.
        return (
          <div className="card-flat stack" style={{ gap: 8 }}>
            <div className="small">
              {model.author_opinion?.state === 'pending'
                ? 'Мнение автора передано.'
                : 'Замечание передано.'}{' '}
              Новая версия придёт от сервиса генерации — ход переделки виден в шапке, слева от
              кнопки запуска.
            </div>
            <div className="micro">
              В очередь эта версия сама не вернётся: единственный выход отсюда — приход версии v+1.
              Если переделка больше не нужна, пост можно удалить — тогда он уйдёт из бэклога, а
              замечание останется в журнале.
            </div>
          </div>
        );

      case 'scheduled':
        return (
          <div className="card-flat stack" style={{ gap: 8 }}>
            <div className="h3">Слот и ключ</div>
            <div className="stack" style={{ gap: 5 }}>
              <span className="meta">
                слот: {publication === null ? '—' : inBrandZone(publication.slot_start_at)}
                {timezone === undefined ? '' : ` (${timezone})`}
              </span>
              <span className="meta">
                крайний срок отправки:{' '}
                {publication === null ? '—' : inBrandZone(publication.slot_deadline_at)}
              </span>
              <span className="meta">
                канал: {publication === null ? '—' : channelWord(publication)}
              </span>
              <span className="meta">ключ выпущен и действителен до конца окна слота</span>
              {autoIssued ? <span className="meta">{AUTO_ISSUED_LINE}</span> : null}
            </div>
            <div className="micro">
              Перед отправкой публикатор ещё раз проверит ключ, каноничность и дневной лимит.
              Публикация — одна попытка по одному ключу, повторов нет. Снятие со слота отзывает ключ
              и возвращает версию в «одобрена»: новый слот потребует нового утверждения.
            </div>
          </div>
        );

      case 'published':
        return (
          <div className="card-flat stack" style={{ gap: 8 }}>
            <div className="h3">Результат</div>
            <div className="stack" style={{ gap: 5 }}>
              <span className="meta">
                {publication === null
                  ? 'работа публикации не найдена'
                  : `message_id ${publication.telegram_message_id ?? '—'} · канал: ${channelWord(publication)} (${publication.chat_id})`}
              </span>
              {publication === null ? null : (
                <span className="meta">
                  {outcomePhrase(publication.outcome_code, publication.outcome_detail)}
                </span>
              )}
              {autoIssued ? <span className="meta">{AUTO_ISSUED_LINE}</span> : null}
            </div>
            {/* Судьба отзыва (G-001): решение человека не имеет права исчезать молча —
                несостоявшийся отзыв на этом экране неотличим от «отзыва не просили». */}
            {publication === null || publication.retraction === null ? null : (
              <div
                className={`val ${
                  publication.retraction.state === 'expired' ||
                  publication.retraction.state === 'failed'
                    ? 'val-err'
                    : 'val-info'
                }`}
              >
                <span>{retractionPhrase(publication.retraction)}</span>
              </div>
            )}
            {publication === null ? null : (
              <a
                className="btn btn-ghost btn-sm"
                href={channelUrl(publication.chat_id, publication.telegram_message_id)}
                target="_blank"
                rel="noreferrer noopener"
                style={{ alignSelf: 'flex-start', textDecoration: 'none' }}
              >
                <IconExternal /> Открыть в канале
              </a>
            )}
            {publication === null ? null : (
              <div className="micro">
                Бот может удалить своё сообщение только в течение 48 часов после публикации. Решение
                всегда за человеком, не за автоматикой.
              </div>
            )}
          </div>
        );

      case 'retracted':
        return (
          <div className="card-flat stack" style={{ gap: 8 }}>
            <div className="h3">Отозван</div>
            <div className="small">
              Публикатор подтвердил удаление сообщения из канала. Состояние терминальное: действий
              над этой версией больше нет.
            </div>
          </div>
        );

      case 'discarded':
        return (
          <div className="card-flat stack" style={{ gap: 8 }}>
            <div className="h3">Удалён</div>
            <div className="small">
              Пост решили не делать. Из бэклога он убран, действий над ним больше нет.
            </div>
            <div className="micro">
              Строка черновика осталась в базе вместе с решениями и журналом: на неё ссылается
              аудит, а он хранится не менее шести месяцев. Найти пост можно фильтром «Архив».
            </div>
          </div>
        );

      case 'superseded':
        return (
          <div className="card-flat stack" style={{ gap: 8 }}>
            <div className="h3">Заменена</div>
            <div className="small">
              Эту версию заменила следующая. Решения принимаются по текущей версии черновика.
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const note = trouble ? null : stateNote();
  const hasContext = issues.length > 0 || trouble || note !== null;

  // ── что можно сделать: закреплённая полоса ─────────────────────────────────────────

  const approveButton = (label: string): JSX.Element => (
    <button
      type="button"
      className={blocked ? 'btn btn-dis' : 'btn btn-pri'}
      disabled={blocked}
      onClick={() => {
        setDialog('approve');
      }}
    >
      {label}
    </button>
  );

  const ghost = (label: string, onClick: () => void): JSX.Element => (
    <button type="button" className="btn btn-ghost" disabled={busy} onClick={onClick}>
      {label}
    </button>
  );

  // Погашенная — `aria-disabled` с причиной в имени и подсказке, как кнопки шапки: выключенная
  // кнопка не получает ни фокуса, ни наведения, и «почему нельзя» было бы недостижимо.
  const rework = (): JSX.Element =>
    reworkOff === null ? (
      ghost('Переделать…', () => {
        setDialog('rework');
      })
    ) : (
      <button
        type="button"
        className="btn btn-dis"
        aria-disabled="true"
        aria-label={offLabel('Переделать', reworkOff)}
        title={offLabel('Переделать', reworkOff)}
      >
        Переделать…
      </button>
    );

  const postpone = (): JSX.Element =>
    ghost('Отложить', () => {
      act('postpone', {});
    });

  /**
   * «Запостить в тестовый канал» — ghost СЛЕВА от главного действия: сперва посмотреть пост в
   * настоящем клиенте, потом утвердить его же. Кнопка не зависит от `busy` полосы и ничего в
   * ней не гасит: тестовая отправка — не решение по версии, и «Утвердить…» остаётся ровно
   * таким, каким было (§9.1). Идёт отправка — кнопка открывает диалог с её ходом.
   */
  const testSend = (): ReactNode => {
    if (!live || !TEST_SEND_STATUSES.has(model.status)) return null;
    return (
      <button
        type="button"
        className={`btn btn-ghost ${styles['testSend']}`}
        aria-label="Запостить в тестовый канал"
        onClick={() => {
          setTestSendOpen(true);
        }}
      >
        <span className={styles['testSendLong']}>Запостить в тестовый канал</span>
      </button>
    );
  };

  /**
   * «Удалить» — в ЛЕВОЙ группе, следом за «Редактировать», как и просил заказчик (26.08).
   *
   * Слева группа режимов экрана, и удаление — не режим; чтобы оно не читалось четвёртым,
   * его отделяет разделитель. Взамен получается свойство, которого не было бы в правой
   * группе: необратимое стоит на противоположном краю полосы от primary. Рука, идущая к
   * «Утвердить…» у правого края, физически не может промахнуться в «Удалить…».
   *
   * Почему не только в правой группе решений: удаление относится к посту целиком, а не к
   * версии, которую сейчас рассматривают, и человек ищет его там же, где «Редактировать», —
   * в наборе действий над постом, а не среди исходов рассмотрения.
   */
  const discard = (): ReactNode => {
    if (!DISCARDABLE.has(model.status)) return null;
    return (
      <>
        <span className={styles['sep']} aria-hidden="true" />
        <button
          type="button"
          className="btn btn-danger btn-sm"
          disabled={busy}
          onClick={() => {
            setDialog('discard');
          }}
        >
          Удалить…
        </button>
      </>
    );
  };

  /**
   * Действия строго по состоянию версии. Где кнопки нет — короткая строка о том, почему её
   * нет: пустая полоса читается как «интерфейс не догрузился».
   */
  const actions = (): ReactNode => {
    // Несостоявшаяся публикация решается в своём блоке: там форма (ссылка на пост из канала),
    // а форму в полосу кнопок не положишь.
    if (trouble) return <span className="micro">Решение — в блоке выше</span>;

    switch (model.status) {
      case 'pending_review':
        return (
          <>
            {postpone()}
            {rework()}
            {testSend()}
            {approveButton('Утвердить…')}
          </>
        );

      case 'approved':
        return (
          <>
            {postpone()}
            {rework()}
            {testSend()}
            {approveButton('Назначить слот…')}
          </>
        );

      case 'deferred':
        return (
          <>
            {rework()}
            {testSend()}
            <button
              type="button"
              className="btn btn-pri"
              disabled={busy}
              onClick={() => {
                act('reopen', {});
              }}
            >
              Вернуть в работу
            </button>
          </>
        );

      case 'scheduled':
        return ghost('Снять с расписания', () => {
          act('unschedule', {});
        });

      case 'published':
        return publication === null
          ? null
          : ghost('Отозвать пост…', () => {
              setDialog('retract');
            });

      // Исходов рассмотрения здесь нет: обратно в очередь версия сама не выходит, её выводит
      // только приход v+1. Единственное действие человека — «Удалить…», и оно в левой группе,
      // среди действий над постом.
      case 'changes_requested':
        return <span className="micro">Ждём новую версию</span>;

      case 'retracted':
      case 'discarded':
        return <span className="micro">Действий больше нет</span>;

      case 'superseded':
        return <span className="micro">Решения — по текущей версии</span>;

      default:
        return <span className="micro">Состояние «{model.status}» действий не предполагает</span>;
    }
  };

  const showsApprove =
    !trouble && (model.status === 'pending_review' || model.status === 'approved');

  /**
   * Мета-строка «в тесте 12:04 МСК · открыть» (§9.1): самая свежая отправка версии — из модели
   * или из слежения, которое свежее модели на время опроса. Все отправки с причинами отказов —
   * подсказкой. Только в боевом режиме, как и кнопка: в тестовом «в тесте» спорило бы с
   * одобрениями, которые сами уходят в тестовый канал.
   */
  const testMeta = (): ReactNode => {
    const latest = tracker.latest;
    if (!live || latest === null) return null;
    const now = nowMs();
    const all = [latest, ...(model.test_sends ?? []).filter((t) => t.id !== latest.id)];
    const title = testSendsTitle(all, now);
    if (!isTerminal(latest.state)) {
      // Не закрытая и уже не сдвинется сама (ключ истёк, публикатор молчит дольше уборки) —
      // «отправляется…» было бы обещанием; честное слово — «нет ответа».
      if (isStalled(latest, now)) {
        return (
          <span
            className={`meta ${styles['testMeta']} ${styles['testFail']}`}
            title={`${title}\nПубликатор не отчитался: проверьте сервис публикатора.`}
            data-testid="test-send-meta"
          >
            в тесте · нет ответа
          </span>
        );
      }
      return (
        <span className={`meta ${styles['testMeta']}`} title={title} data-testid="test-send-meta">
          <span className={styles['testDot']} aria-hidden="true" />в тесте · отправляется…
        </span>
      );
    }
    const when = momentMsk(latest.finished_at ?? latest.created_at, now);
    if (latest.state === 'sent') {
      const link = testSendLink(latest);
      // Расхождение — цветом предупреждения и одним словом; чем разошлось — в подсказке.
      const warn = deviated(latest);
      return (
        <span
          className={`meta ${styles['testMeta']}${warn ? ` ${styles['testWarn']}` : ''}`}
          title={title}
          data-testid="test-send-meta"
        >
          {warn ? `в тесте ${when} · расхождение` : `в тесте ${when}`}
          {link === null ? null : (
            <>
              {' · '}
              <a href={link} target="_blank" rel="noreferrer noopener">
                открыть
              </a>
            </>
          )}
        </span>
      );
    }
    return (
      <span
        className={`meta ${styles['testMeta']} ${styles['testFail']}`}
        title={title}
        data-testid="test-send-meta"
      >
        {`в тесте ${when} · не вышло`}
      </span>
    );
  };

  return (
    <>
      {hasContext ? (
        <div className={styles['context']}>
          {issues.map((i) => (
            <div key={`${i.level}:${i.code}`} className={issueClass(i.level)}>
              <span>{i.message}</span>
            </div>
          ))}
          {trouble ? (
            <FailedPanel
              model={model}
              onChanged={onChanged}
              onApproveNewSlot={() => {
                setDialog('approve');
              }}
            />
          ) : (
            note
          )}
        </div>
      ) : null}

      {/* role="toolbar", а не "group": это набор команд над одним объектом, и именно так его
          читает скринридер. Тем же ролью размечена панель разметки в редакторе исходника. */}
      <div className={styles['bar']} role="toolbar" aria-label="Решение">
        {/* Отказ показывается ЗДЕСЬ, а не в блоке выше: кнопку нажали здесь, и на длинном
            посте блок выше в этот момент за пределами экрана. */}
        {error === null ? null : (
          <div className="val val-err">
            <span>{error}</span>
          </div>
        )}
        <div className={styles['row']}>
          {/* Слева — что я делаю С ПОСТОМ: режим экрана (приходит пропом от DraftPanel) и
              удаление. Справа — чем заканчивается рассмотрение. Разные вопросы, разные края. */}
          <div className={styles['lead']}>
            {leading}
            {discard()}
          </div>
          <div className={styles['gap']} />
          {blocked && showsApprove ? (
            <span className="micro">
              Блокирует утверждение: {blocking.map((i) => i.code).join(', ')}
            </span>
          ) : null}
          {testMeta()}
          <div className={styles['acts']}>{actions()}</div>
        </div>
      </div>

      {testSendOpen ? (
        <TestSendDialog model={model} tracker={tracker} onClose={closeTestSend} />
      ) : null}

      {dialog === 'approve' ? (
        <ApproveDialog
          model={model}
          screenBrand={screenBrand}
          issues={issues}
          richAlways={richAlways}
          onChanged={onChanged}
          onClose={() => {
            setDialog('none');
          }}
        />
      ) : null}

      {dialog === 'rework' ? (
        <RequestChangesDialog
          model={model}
          onChanged={onChanged}
          onClose={() => {
            setDialog('none');
          }}
        />
      ) : null}

      {dialog === 'discard' ? (
        <DiscardDialog
          model={model}
          onChanged={onChanged}
          onClose={() => {
            setDialog('none');
          }}
        />
      ) : null}

      {dialog === 'retract' && publication !== null ? (
        <RetractDialog
          model={model}
          screenBrand={screenBrand}
          publication={publication}
          onChanged={onChanged}
          onClose={() => {
            setDialog('none');
          }}
        />
      ) : null}
    </>
  );
}
